import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  AdjustmentReason,
  createMyAdjustmentApi,
  createMyDayAdjustmentApi,
  hhmm,
  MY_ADJUSTMENT_REASONS,
  MyTimesheetDay,
  timeOf,
} from "../api/timekeepingApi";

import "../pages/Ponto.css";

export type SheetMode = "INCLUIR" | "DIA" | "DESCONSIDERAR" | "ABONO";

export type AdjustmentSheetInitial = {
  /** YYYY-MM-DD */
  day: string;
  mode?: SheetMode;
  minute?: number;
  reasonCode?: AdjustmentReason;
  /** O dia no espelho (sugestões pela escala, marcações para desconsiderar). */
  dayData?: MyTimesheetDay;
};

type Props = {
  initial: AdjustmentSheetInitial;
  onClose: () => void;
  onSent: () => void;
};

const LABELS = ["Entrada", "Saída para o intervalo", "Volta do intervalo", "Saída"];

const toMinute = (value: string): number | null => {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

const formatDay = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString("pt-BR", { timeZone: "UTC", weekday: "long", day: "2-digit", month: "2-digit" });

/**
 * Pedido de correção do ponto ao DP: incluir uma marcação que faltou, informar o
 * dia inteiro, desconsiderar uma marcação errada ou justificar uma falta. O
 * horário sugerido vem da escala. O pedido nasce pendente e o DP decide.
 */
export default function AdjustmentSheet({ initial, onClose, onSent }: Props) {
  const day = initial.dayData;
  const marksCount = day?.effectivePunches.length ?? 0;
  const suggested = day?.suggestedMarks ?? [];
  const originals = (day?.effectivePunches ?? []).filter((p) => p.source === "ORIGINAL" && p.punchId);

  const options = useMemo(() => {
    const list: Array<{ value: SheetMode; label: string; hint: string }> = [
      { value: "INCLUIR", label: "Faltou uma marcação", hint: "Esqueci de marcar, ou tentei e não foi." },
    ];
    if (!day || marksCount === 0) list.push({ value: "DIA", label: "O dia inteiro ficou sem marcação", hint: "Informe os horários de entrada, intervalo e saída." });
    if (originals.length) list.push({ value: "DESCONSIDERAR", label: "Uma marcação está errada", hint: "Marcação repetida ou feita por engano." });
    if (day && (day.kind === "FALTA" || day.lateMinutes > 0)) list.push({ value: "ABONO", label: "Justificar falta ou atraso", hint: "Explique o motivo; o DP pode pedir comprovante." });
    return list;
  }, [day, marksCount, originals.length]);

  const nextMissing = suggested[marksCount] ?? null;
  const [mode, setMode] = useState<SheetMode>(initial.mode ?? options[0].value);
  const [time, setTime] = useState(hhmm(initial.minute ?? nextMissing ?? null));
  const [dayTimes, setDayTimes] = useState<string[]>(
    (suggested.length ? suggested : [480, 720, 780, 1020]).map((m) => hhmm(m)),
  );
  const [targetPunchId, setTargetPunchId] = useState(originals[originals.length - 1]?.punchId ?? "");
  const [reasonCode, setReasonCode] = useState<AdjustmentReason>(initial.reasonCode ?? (mode === "ABONO" ? "AUSENCIA_JUSTIFICADA" : "ESQUECIMENTO"));
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);

  /** Turno que vira o dia: horário antes do início −3h é da madrugada seguinte. */
  const relative = (minute: number) => (day?.startMinute != null && day.endMinute != null && day.endMinute > 1440 && minute < day.startMinute - 180 ? minute + 1440 : minute);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (reasonCode === "OUTRO" && reason.trim().length < 10) return toast.error("Descreva o motivo (mínimo de 10 caracteres).");
    setSending(true);
    try {
      const common = { day: initial.day, reasonCode, reason: reason.trim() || undefined };
      if (mode === "INCLUIR") {
        const minute = toMinute(time);
        if (minute === null) throw new Error("Informe o horário.");
        await createMyAdjustmentApi({ ...common, type: "INCLUSAO_MARCACAO", minuteOfDay: relative(minute) });
      } else if (mode === "DIA") {
        const minutes = dayTimes.map(toMinute).filter((m): m is number => m !== null);
        if (minutes.length < 2) throw new Error("Informe ao menos a entrada e a saída.");
        if (minutes.length % 2 === 1) throw new Error("Informe os horários em pares: entrada e saída.");
        await createMyDayAdjustmentApi({ ...common, marks: minutes.map(relative) });
      } else if (mode === "DESCONSIDERAR") {
        if (!targetPunchId) throw new Error("Escolha a marcação.");
        await createMyAdjustmentApi({ ...common, type: "DESCONSIDERAR_MARCACAO", targetPunchId, reasonCode: reasonCode === "ESQUECIMENTO" ? "MARCACAO_INDEVIDA" : reasonCode });
      } else {
        await createMyAdjustmentApi({ ...common, type: day?.kind === "FALTA" ? "ABONO_FALTA" : "ABONO_ATRASO", minutes: day?.kind === "FALTA" ? undefined : day?.lateMinutes || undefined });
      }
      onSent();
    } catch (error: any) {
      toast.error(error?.message || "Não foi possível enviar o pedido.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="colabSheet__overlay">
      <form className="colabSheet" role="dialog" aria-modal="true" aria-labelledby="adjSheetTitle" onSubmit={submit}>
        <div className="colabSheet__head">
          <h2 id="adjSheetTitle" className="colabSheet__title">
            Corrigir {formatDay(initial.day)}
          </h2>
          <button type="button" className="colabSheet__close" aria-label="Fechar" onClick={onClose} disabled={sending}>
            ×
          </button>
        </div>

        {day && day.expectedSource !== "NENHUMA" && day.startMinute !== null ? (
          <p className="colabNotice">
            Previsto: {hhmm(day.startMinute)} às {hhmm(day.endMinute)}
            {day.effectivePunches.length ? ` · marcado: ${day.effectivePunches.map((p) => timeOf(p.at)).join(", ")}` : " · nenhuma marcação"}
          </p>
        ) : null}

        <div className="colabChoices" role="radiogroup" aria-label="O que aconteceu?">
          {options.map((o) => (
            <label key={o.value} className={`colabChoice ${mode === o.value ? "colabChoice--on" : ""}`}>
              <input
                type="radio"
                name="adjMode"
                checked={mode === o.value}
                onChange={() => {
                  setMode(o.value);
                  if (o.value === "ABONO") setReasonCode("AUSENCIA_JUSTIFICADA");
                  if (o.value === "DESCONSIDERAR") setReasonCode("MARCACAO_INDEVIDA");
                }}
              />
              <span>
                {o.label}
                <small>{o.hint}</small>
              </span>
            </label>
          ))}
        </div>

        {mode === "INCLUIR" ? (
          <div className="colabField" style={{ marginBottom: 14 }}>
            <label htmlFor="adjTime">
              Horário da marcação{nextMissing !== null && initial.minute === undefined ? ` (${LABELS[marksCount] ?? "marcação"} prevista)` : ""}
            </label>
            <input id="adjTime" type="time" className="colabInput" value={time} onChange={(e) => setTime(e.target.value)} required />
          </div>
        ) : null}

        {mode === "DIA" ? (
          <div className="colabTimes">
            {LABELS.map((label, i) => (
              <div key={label} className="colabField">
                <label htmlFor={`adjDay${i}`}>{label}</label>
                <input
                  id={`adjDay${i}`}
                  type="time"
                  className="colabInput"
                  value={dayTimes[i] ?? ""}
                  onChange={(e) => setDayTimes((list) => list.map((v, j) => (j === i ? e.target.value : v)))}
                />
              </div>
            ))}
          </div>
        ) : null}

        {mode === "DESCONSIDERAR" ? (
          <div className="colabField" style={{ marginBottom: 14 }}>
            <label htmlFor="adjTarget">Marcação a desconsiderar</label>
            <select id="adjTarget" className="colabInput" value={targetPunchId} onChange={(e) => setTargetPunchId(e.target.value)}>
              {originals.map((p) => (
                <option key={p.punchId} value={p.punchId}>
                  {timeOf(p.at)} · nº {p.nsr}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="colabField" style={{ marginBottom: 14 }}>
          <label htmlFor="adjReason">Motivo</label>
          <select id="adjReason" className="colabInput" value={reasonCode} onChange={(e) => setReasonCode(e.target.value as AdjustmentReason)}>
            {MY_ADJUSTMENT_REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        <div className="colabField" style={{ marginBottom: 16 }}>
          <label htmlFor="adjText">Explique {reasonCode === "OUTRO" ? "(obrigatório)" : "(opcional)"}</label>
          <textarea id="adjText" className="colabTextarea" maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>

        <button type="submit" className="colabBtn colabBtn--primary" disabled={sending}>
          {sending ? "Enviando..." : "Enviar pedido ao DP"}
        </button>
      </form>
    </div>
  );
}
